/**
 * AccounsellingStreamingService
 *
 * Progressive, batched fetching for the heavy AC-Counselling admin lists. Mirrors the
 * TaskStreamingService pattern: a client-side Skip/Take pagination generator that yields one
 * batch (array) per page as it arrives, so the UI can paint rows while later pages are still
 * loading. The backend list endpoints are cache-composed (fast) and offset-paginated.
 */
import { CareCaseService } from './CareCaseService';
import { LeaderService } from './LeaderService';
export class AccounsellingStreamingService {
    constructor(http) {
        this.careCaseService = new CareCaseService(http);
        this.leaderService = new LeaderService(http);
    }
    /**
     * Stream care cases in batches (server offset pagination). Yields one `CareCaseListView[]`
     * per page until the server reports no more rows.
     */
    async *streamCases(filter, chunkSize = 50) {
        let skip = 0;
        let hasMore = true;
        while (hasMore) {
            const response = await this.careCaseService.listViewRaw({ ...filter, skip, take: chunkSize });
            const items = response.data?.items ?? [];
            if (items.length > 0)
                yield items;
            if (response.data?.hasMore === false || items.length < chunkSize)
                hasMore = false;
            else
                skip += chunkSize;
        }
    }
    /**
     * Stream leaders in batches (server offset pagination).
     */
    async *streamLeaders(filter, chunkSize = 50) {
        let skip = 0;
        let hasMore = true;
        while (hasMore) {
            const response = await this.leaderService.listRaw(filter?.typeId, filter?.zoneId, filter?.needId, filter?.search, skip, chunkSize);
            const items = response.data ?? [];
            if (items.length > 0)
                yield items;
            if (response.hasMore === false || items.length < chunkSize)
                hasMore = false;
            else
                skip += chunkSize;
        }
    }
    /**
     * Stream the cases assigned to a leader, in batches. This endpoint returns the full (usually
     * small) set, so it is fetched once and chunked client-side.
     */
    async *streamCasesByLeader(userId, chunkSize = 50) {
        const all = await this.leaderService.getCases(userId);
        for (let i = 0; i < all.length; i += chunkSize) {
            yield all.slice(i, i + chunkSize);
        }
    }
    /** Collect a whole case stream into one array, reporting progress per batch. */
    async loadAllCases(filter, chunkSize = 50, onProgress) {
        const all = [];
        for await (const chunk of this.streamCases(filter, chunkSize)) {
            all.push(...chunk);
            onProgress?.(all.length);
        }
        return all;
    }
}
//# sourceMappingURL=AccounsellingStreamingService.js.map